import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Divider,
  Chip,
  Stack,
  TextField,
  FormControlLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PhoneIcon from '@mui/icons-material/Phone';
import BuildIcon from '@mui/icons-material/Build';
import NotesIcon from '@mui/icons-material/Notes';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { format, parseISO } from 'date-fns';
import type { SelectedServiceData } from './ServiceStep';
import type { NoteCategory } from '@/types';
import { formatDuration } from '@/utils/duration';
import { guideAnchor } from '@/lib/guides/engine';
import {
  ChecklistTemplateSelect,
  type CreateChecklistSelection,
} from './ChecklistTemplateSelect';
import type { AdditionalQuestion } from '@/hooks/useTenantSettings';
import { ServiceBookCheckedStatus } from '../ServiceBookChecklistsCard';
import { distinctServiceMechanics } from '../WorkOrderMechanicsSummary';
import { dateFnsLocale } from '@/utils/locale';

/** Customer-facing confirmation methods offered at work-order create (#2126). */
export type CreateConfirmationMethod = 'phone' | 'email' | 'in_person';

const CREATE_CONFIRMATION_OPTIONS: {
  value: CreateConfirmationMethod;
  labelKey: 'confirmationMethod.phone' | 'confirmationMethod.email' | 'confirmationMethod.inPerson';
}[] = [
  { value: 'phone', labelKey: 'confirmationMethod.phone' },
  { value: 'email', labelKey: 'confirmationMethod.email' },
  { value: 'in_person', labelKey: 'confirmationMethod.inPerson' },
];

interface LocalNote {
  category: NoteCategory;
  content: string;
  authorName: string;
  createdAt: string;
}

interface SummaryStepProps {
  /** 'quote' hides scheduling info and surfaces the quote validity field (#848/#849). */
  mode?: 'workOrder' | 'quote';
  vehicleRegNumber: string;
  vehicleDescription: string;
  customerDisplayName: string;
  isCreatingNewVehicle: boolean;
  isCreatingNewCustomer: boolean;
  scheduledAtStr: string;
  vehicleDropoffAtStr?: string;
  /** 'floating' shows the day without a time of day (#1138). */
  schedulingType?: 'exact' | 'floating';
  pickupAtStr?: string;
  customerWaits: boolean;
  allowMinorMaintenance: boolean;
  jointInspectionRequested: boolean;
  selectedServices: SelectedServiceData[];
  mechanicNames: Record<string, string>;
  liftNames: Record<string, string>;
  localNotes: LocalNote[];
  showManagerNotes?: boolean;
  preapprovedLimitEuros?: number | null;
  /** Quote validity in days (#849) — editable in quote mode. */
  quoteValidityDaysInput?: string;
  onQuoteValidityDaysChange?: (value: string) => void;
  /** How the booking was confirmed with the customer; `null` = leave unconfirmed (#2126). */
  confirmationMethod?: CreateConfirmationMethod | null;
  onConfirmationMethodChange?: (value: CreateConfirmationMethod | null) => void;
  /** Optional inspection / service-book lists to attach after create (#2128). */
  selectedChecklists?: CreateChecklistSelection[];
  onSelectedChecklistsChange?: (value: CreateChecklistSelection[]) => void;
  /** Workshop reception questions; only active ones are shown (#2130). */
  additionalQuestions?: AdditionalQuestion[];
  additionalQuestionAnswers?: Record<string, string>;
  onAdditionalQuestionAnswerChange?: (questionId: string, answer: string) => void;
  /** Local create-flow value for service-book checked (#2339). */
  serviceBookChecked?: boolean;
  onServiceBookCheckedChange?: (value: boolean) => void;
}

export function SummaryStep({
  mode = 'workOrder',
  vehicleRegNumber,
  vehicleDescription,
  customerDisplayName,
  isCreatingNewVehicle,
  isCreatingNewCustomer,
  scheduledAtStr,
  vehicleDropoffAtStr,
  schedulingType = 'exact',
  pickupAtStr,
  customerWaits,
  allowMinorMaintenance,
  jointInspectionRequested,
  selectedServices,
  mechanicNames,
  liftNames,
  localNotes,
  showManagerNotes,
  preapprovedLimitEuros,
  quoteValidityDaysInput,
  onQuoteValidityDaysChange,
  confirmationMethod = null,
  onConfirmationMethodChange,
  selectedChecklists = [],
  onSelectedChecklistsChange,
  additionalQuestions = [],
  additionalQuestionAnswers = {},
  onAdditionalQuestionAnswerChange,
  serviceBookChecked = false,
  onServiceBookCheckedChange,
}: SummaryStepProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const { t: tChecklists } = useTranslation('checklists');
  const isQuote = mode === 'quote';

  const scheduledDate = scheduledAtStr ? parseISO(scheduledAtStr) : null;
  // Show the customer's requested pickup separately only when it differs from
  // the booking's scheduled time (#529).
  const showPickupAt = !!pickupAtStr && pickupAtStr !== scheduledAtStr;
  const pickupAt = pickupAtStr ? parseISO(pickupAtStr) : null;

  const totalDuration = selectedServices.reduce(
    (sum, s) => sum + (s.estimatedDurationHours ?? 0),
    0,
  );

  // Distinct mechanics across service rows (#3092). Lift is still one per
  // work order (#1993). Empty is the normal case — planning assigns them.
  const assignedMechanicNames = distinctServiceMechanics(selectedServices).flatMap((entry) => {
    if (!entry.mechanicId) return [];
    const name = mechanicNames[entry.mechanicId];
    return [name || entry.mechanicId];
  });
  const assignedLiftId = selectedServices[0]?.liftId ?? '';
  const assignedLiftName = assignedLiftId ? liftNames[assignedLiftId] : undefined;

  const activeAdditionalQuestions = additionalQuestions.filter((q) => q.isActive);
  const internalNotesCount = localNotes.filter((n) => n.category === 'internal').length;
  const managerNotesCount = localNotes.filter((n) => n.category === 'manager').length;
  // Customer notes are left out (#3382): the dialog's notes column shows them
  // in full, and here the label swallowed the first line break and long text
  // ran past the edge. Only the internal and manager counts remain.
  const hasNotes = internalNotesCount > 0 || (showManagerNotes && managerNotesCount > 0);

  return (
    <Box {...guideAnchor('wo-create.summary')} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Vehicle */}
      <SummarySection icon={<DirectionsCarIcon fontSize="small" />} label={t('vehicle')}>
        <Typography variant="body1" fontWeight={600}>
          {vehicleRegNumber}
          {vehicleDescription && ` — ${vehicleDescription}`}
        </Typography>
        {isCreatingNewVehicle && (
          <Chip label={tc('new')} size="small" color="info" variant="outlined" sx={{ ml: 1 }} />
        )}
      </SummarySection>

      <Divider />

      {/* Customer */}
      <SummarySection icon={<PersonIcon fontSize="small" />} label={t('customer')}>
        <Typography variant="body1">{customerDisplayName}</Typography>
        {isCreatingNewCustomer && (
          <Chip label={tc('new')} size="small" color="info" variant="outlined" sx={{ ml: 1 }} />
        )}
      </SummarySection>

      <Divider />

      {/* Schedule — work orders only; quotations are not scheduled (#848) */}
      {!isQuote && (
        <>
          <SummarySection icon={<CalendarTodayIcon fontSize="small" />} label={t('create.stepScheduling')}>
            <Typography variant="body1">
              {scheduledDate
                ? schedulingType === 'floating'
                  ? t('create.floatingScheduleSummary', {
                      date: format(scheduledDate, 'EEEE d.M.yyyy', { locale: dateFnsLocale() }),
                    })
                  : format(scheduledDate, 'EEEE d.M.yyyy HH:mm', { locale: dateFnsLocale() })
                : '-'}
            </Typography>
            {customerWaits && (
              <Chip label={t('create.customerWaits')} size="small" color="info" sx={{ ml: 1 }} />
            )}
            {allowMinorMaintenance && (
              <Chip label={t('create.allowMinorMaintenance')} size="small" color="success" sx={{ ml: 1 }} />
            )}
            {jointInspectionRequested && (
              <Chip label={t('create.jointInspectionRequested')} size="small" color="info" sx={{ ml: 1 }} />
            )}
          </SummarySection>

          {vehicleDropoffAtStr && (
            <>
              <Divider />
              <SummarySection icon={<DirectionsCarIcon fontSize="small" />} label={t('create.vehicleDropoffAt')}>
                <Typography variant="body1">
                  {format(parseISO(vehicleDropoffAtStr), 'EEEE d.M.yyyy HH:mm', { locale: dateFnsLocale() })}
                </Typography>
              </SummarySection>
            </>
          )}

          {showPickupAt && pickupAt && (
            <>
              <Divider />
              <SummarySection
                icon={<CalendarTodayIcon fontSize="small" />}
                label={t('requestedPickupAt')}
              >
                <Typography variant="body1">
                  {format(pickupAt, 'EEEE d.M.yyyy HH:mm', { locale: dateFnsLocale() })}
                </Typography>
              </SummarySection>
            </>
          )}

          <Divider />

          <SummarySection icon={<PhoneIcon fontSize="small" />} label={t('create.confirmationMethodQuestion')}>
            <RadioGroup
              aria-label={t('create.confirmationMethodQuestion')}
              sx={{ width: '100%' }}
              value={confirmationMethod ?? 'unconfirmed'}
              onChange={(_e, value) => {
                if (value === 'unconfirmed') {
                  onConfirmationMethodChange?.(null);
                  return;
                }
                onConfirmationMethodChange?.(value as CreateConfirmationMethod);
              }}
            >
              <FormControlLabel
                value="unconfirmed"
                control={<Radio size="small" />}
                label={t('create.confirmationMethodUnconfirmed')}
              />
              {CREATE_CONFIRMATION_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" />}
                  label={t(option.labelKey)}
                />
              ))}
            </RadioGroup>
          </SummarySection>

          <Divider />
        </>
      )}

      {/* Quote validity — quotations only (#849) */}
      {isQuote && (
        <>
          <SummarySection icon={<EventBusyIcon fontSize="small" />} label={t('create.quoteValidity')}>
            <TextField
              type="number"
              size="small"
              value={quoteValidityDaysInput ?? ''}
              onChange={(e) => onQuoteValidityDaysChange?.(e.target.value)}
              helperText={t('create.quoteValidityHint')}
              slotProps={{
                htmlInput: { min: 1, max: 365, step: 1, ...guideAnchor('wo-create.quoteValidity') },
              }}
              sx={{ maxWidth: 220 }}
            />
          </SummarySection>
          <Divider />
        </>
      )}

      {/* Pre-approved additional work limit (#66) */}
      {preapprovedLimitEuros != null && preapprovedLimitEuros > 0 && (
        <>
          <SummarySection icon={<BuildIcon fontSize="small" />} label={t('preapprovedLimit.title')}>
            <Typography variant="body1">
              {t('preapprovedLimit.fullText', { amount: preapprovedLimitEuros.toFixed(2) })}
            </Typography>
          </SummarySection>
          <Divider />
        </>
      )}

      {/* Services */}
      <SummarySection icon={<BuildIcon fontSize="small" />} label={t('create.stepServices')}>
        <Stack spacing={0.5}>
          {selectedServices.map((svc) => (
            <Box key={svc.rowId} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={svc.serviceName} size="small" />
              {svc.title?.trim() && svc.title.trim() !== svc.serviceName && (
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  {svc.title.trim()}
                </Typography>
              )}
              {svc.estimatedDurationHours != null && (
                <Typography variant="caption" color="text.secondary">
                  {formatDuration(svc.estimatedDurationHours)}
                </Typography>
              )}
            </Box>
          ))}
          {/* Distinct mechanics and one lift cover the work order
              (#3092/#1993), so they are stated once here rather than repeated
              after every service. */}
          {assignedMechanicNames.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('create.assignMechanic')}: {assignedMechanicNames.join(', ')}
            </Typography>
          )}
          {assignedLiftName && (
            <Typography variant="body2" color="text.secondary">
              {t('create.assignLift')}: {assignedLiftName}
            </Typography>
          )}
          {totalDuration > 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('create.totalDuration')}: {totalDuration} {t('create.hoursSuffix')}
            </Typography>
          )}
        </Stack>
      </SummarySection>

      <Divider />

      {/* Service book (#2339) — always shown, not gated by selected services */}
      <SummarySection icon={<MenuBookIcon fontSize="small" />} label={tChecklists('serviceBook.title')}>
        <ServiceBookCheckedStatus
          checked={serviceBookChecked}
          onCheckedChange={(value) => onServiceBookCheckedChange?.(value)}
        />
      </SummarySection>

      <Divider />

      {/* Reception additional questions (#2130) — optional; hidden when none are defined */}
      {activeAdditionalQuestions.length > 0 && (
        <>
          <SummarySection
            icon={<QuestionAnswerIcon fontSize="small" />}
            label={t('create.additionalQuestionsSection')}
          >
            <Stack spacing={1.5} sx={{ width: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                {t('create.additionalQuestionsHint')}
              </Typography>
              {activeAdditionalQuestions.map((question) => (
                <TextField
                  key={question.id}
                  size="small"
                  fullWidth
                  label={question.label}
                  value={additionalQuestionAnswers[question.id] ?? ''}
                  onChange={(e) => onAdditionalQuestionAnswerChange?.(question.id, e.target.value)}
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                />
              ))}
            </Stack>
          </SummarySection>
          <Divider />
        </>
      )}

      {/* Inspection and service-book lists (#2128) — optional */}
      <SummarySection icon={<PlaylistAddCheckIcon fontSize="small" />} label={t('create.checklistsSection')}>
        <ChecklistTemplateSelect
          value={selectedChecklists}
          onChange={(next) => onSelectedChecklistsChange?.(next)}
          serviceIds={selectedServices.map((s) => s.serviceId)}
        />
      </SummarySection>

      {/* Notes */}
      {hasNotes && (
        <>
          <Divider />
          <SummarySection icon={<NotesIcon fontSize="small" />} label={t('create.notesSection')}>
            <Stack spacing={0.5}>
              {internalNotesCount > 0 && (
                <Typography variant="body2">
                  {t('notesHistory.internalNotes')}: {t('notesHistory.notesCount', { count: internalNotesCount })}
                </Typography>
              )}
              {showManagerNotes && managerNotesCount > 0 && (
                <Typography variant="body2">
                  {t('notesHistory.managerNotes')}: {t('notesHistory.notesCount', { count: managerNotesCount })}
                </Typography>
              )}
            </Stack>
          </SummarySection>
        </>
      )}
    </Box>
  );
}

function SummarySection({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {icon}
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', pl: 3.5 }}>
        {children}
      </Box>
    </Box>
  );
}
